/**
 * 武将（東日本）— 織田信長・武田信玄・上杉謙信・北条氏康
 *
 * docs/01-ゲームデザイン.md §8 の能力表（現行版）に対応する。
 * 常設の補正は **すべて手勢札の枚数**（holeCardMod）で表現する。
 * docs/06-賭けと軍配勝負.md §4 の実測では、役を1段階上げる補正は
 * 防御側 +1 だけで勝率が 50.6%→82.8% に飛び、許容上限の 75.8% を超える。
 * そのため `formationMod` は常設の補正には使わず、下剋上のような
 * 一撃の逆転にのみ用いる。枚数の目安も §4 の表どおりで、
 * 武将能力は ±1枚、城は居城 +1枚 / 天守 +2枚。合計は 2〜4枚に丸める。
 *
 * 史実の書きぶりは docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化しない
 *   - 合戦だけでなく、法度・検地・治水・交易など統治と生活の事実を必ず入れる
 *   - 通説と異説がある事項は caveat に書く
 *
 * color は家紋の色を史料的に確定できないため、盤上での判別を優先した
 * ゲーム上の割り当てである（赤備え・毘沙門天の旗など、由来のあるものは
 * それに寄せている）。
 */

import type { Warlord } from '../types';

/** 尾張(4)から始まる。流通と鉄砲の早期導入を能力にしている。 */
export const NOBUNAGA: Warlord = {
  id: 'wl:nobunaga',
  name: '織田信長',
  reading: 'おだ のぶなが',
  clan: '織田氏',
  color: '#7B2D26', // 弁柄色。木瓜紋
  startSquare: 4,
  ability: {
    name: '天下布武',
    text: '自分が加わる合戦の軍札に鉄砲が入る（鉄砲伝来より早く扱える）。津島・熱田の流通収入により、章が変わるたびに兵を1得る。',
    // 当初は when: 'turn'（毎ターン）だったが、14回発火して累計+14兵になり、
    // 勝率が 22.4% → 61.2% に跳ねた（100局・AI対戦での実測）。
    // 章ごと（3回）に刻んで、開始時一括の能力と釣り合う量にした。
    rules: [{ when: 'chapter', effects: [{ t: 'gain', res: 'hei', n: 1 }] }],
    deckInject: 'teppou',
  },
  note: {
    summary:
      '尾張の織田弾正忠家の出身。尾張・美濃を経て京へ入り、畿内を中心に広い範囲を支配した。関所の撤廃や城下町の楽市令など流通に関わる政策を出し、1582年に家臣の明智光秀の襲撃を受けて京都本能寺で死んだ。',
    detail: {
      1: '尾張（いまの愛知県）の大名です。となりの美濃をとり、京にのぼって、たくさんの国をおさめました。市場の税をなくして商人をあつめるしくみをつくりました。1582年、家来の明智光秀におそわれて、京都の本能寺で亡くなりました。',
      2: '尾張の守護代織田氏の一族、弾正忠家に生まれた。1560年に桶狭間で今川義元の軍を破り、1567年に美濃を取って稲葉山城を岐阜城と改めた。1568年に足利義昭を奉じて京へ入り、1573年には義昭を京から追って室町幕府は事実上滅んだ。加納（1567年）や安土（1577年）の城下に楽市令を出し、座の特権や市場の税を否定して商人を集め、領国内の関所を撤廃して人と物の動きを速めた。一方で伊勢長島や越前の一向一揆、比叡山延暦寺との戦いでは多数の死者が出ている。1582年、京都本能寺で明智光秀の襲撃を受けて死去した。',
      3: '尾張守護代織田大和守家の被官である弾正忠家の出身で、家格としては高くない立場から尾張一国の統一を果たした。永禄3年(1560)の桶狭間の戦いで今川義元を討ち、永禄10年(1567)に美濃斎藤氏を降して稲葉山城を岐阜城と改称、永禄11年(1568)に足利義昭を奉じて上洛した。元亀年間には浅井・朝倉、石山本願寺、比叡山延暦寺、伊勢長島の一向一揆と各地で争い、天正元年(1573)に義昭を追放して室町幕府は事実上終わった。経済政策としては、加納・安土などに出した楽市令で座の特権と市場課税を否定し、領国内の関所を撤廃、撰銭令によって銭の交換比率を定めた。土地は指出検地（領主の自己申告による把握）が中心で、後の太閤検地のような一律の丈量とは性格が異なる。天正10年(1582)6月2日、京都本能寺で明智光秀の軍に襲われて死亡した。近年の研究では、信長を既存秩序を壊した「革命児」とみる理解が見直されつつあり、朝廷や幕府の権威を利用しながら段階的に勢力を広げた側面が強調されている。',
    },
    year: [1534, 1582],
    keywords: ['織田信長', '尾張', '楽市令', '上洛', '本能寺の変', '関所撤廃'],
    caveat:
      '桶狭間の戦いを「迂回しての奇襲」とする通説は、近年の研究では正面から攻めかかったとする説が有力です。長篠の戦いの「三段撃ち」も同時代の史料に裏付けがありません。楽市も信長が考え出したものではなく、1549年に六角定頼が観音寺城下の石寺に出した例が知られ、今川氏真も行っています。信長が兵農分離を徹底したという理解も現在は見直されています。',
    gameNote:
      '「鉄砲を1章早く扱える」はゲーム上の便宜です。鉄砲の伝来は1543年で、信長だけが他より早く手に入れたわけではありません。徴兵が軽い能力は、津島・熱田の流通収入が織田氏の軍事力を支えたという理解を遊びに落とし込んだもので、史実の徴兵制度を再現したものではありません。',
  },
};

/** 甲斐(21)から始まる。docs/01 §8 の「自領で守るとき手勢札+1」をそのまま持つ。 */
export const SHINGEN: Warlord = {
  id: 'wl:shingen',
  name: '武田信玄',
  reading: 'たけだ しんげん',
  clan: '武田氏',
  color: '#C1272D', // 緋色。武田菱／赤備えにちなむ
  startSquare: 21,
  ability: {
    name: '甲州法度',
    text: '自分の領国で守るとき、手勢札が1枚増える。攻めるときや横槍で割り込んだときは増えない。守り勝つと兵を2得る。',
    // 防御側は定義上つねに自分の領国にいるので、条件は 'defend' で足りる。
    // 常設の補正に formationMod を使わない理由は docs/06 §4 の実測（+1 で勝率 82.8%）による。
    // 守勢の能力は「攻められないと発火しない」ぶん、開始時の資源より弱い。
    // 実測で 20.0%（4人卓の均等は 25%）だったので、開始時の石高を足した
    // 開始時の石高を足しても順位は動かなかった（600局で 14.7% と最下位）。
    // 占有型の得点では「守れる」だけでは点にならないので、
    // **守り勝ちを石高と兵に換えて、国を取る力に接続する**
    rules: [
      {
        when: 'winDefend',
        // 石高1・兵1 から兵2 に変えた。一度は「下がった」と判断して戻したが、
        // それは1回の600局を見た誤りだった。3つの seed 系列で対にして測ると
        // **3組すべてで改善**していた（+1.6 / +2.6 / +3.4pt、平均 +2.5pt）。
        // 守り勝ちが兵になれば、次の手番で攻めに出られる（`docs/04`）
        effects: [{ t: 'gain', res: 'hei', n: 2 }],
      },
    ],
    holeCardRules: [{ when: 'defend', n: 1 }],
  },
  note: {
    summary:
      '甲斐の武田氏の当主。1541年に父信虎を駿河へ追って家督を継いだ。分国法「甲州法度之次第」を定め、釜無川の治水や金山の開発を進めた。信濃へ進出して上杉氏と川中島で数度戦い、1573年に西へ向かう途中で病没した。',
    detail: {
      1: '甲斐（いまの山梨県）の大名です。名前は晴信といい、信玄はおぼうさんになってからの名です。国のきまりをつくり、川があふれないように堤をきずきました。信濃をめぐって上杉謙信と川中島で何度も戦い、1573年に病気で亡くなりました。',
      2: '甲斐の守護武田氏の当主。名は晴信で、信玄は出家後の号。1541年に父信虎を駿河へ追放して家督を継いだ。1547年に分国法「甲州法度之次第」を定め、家臣どうしの争いを実力で解決させず、当主の裁定に従わせた。この法には、当主自身も法に背いたときは訴えてよいとする条文がある。釜無川と御勅使川の合流点に堤を築く治水（信玄堤と呼ばれる）、黒川金山などの鉱山開発、甲州金の鋳造も進めた。八ヶ岳のふもとには棒道と呼ばれるまっすぐな道があり、信玄がつくったと伝えられているが、それをたしかめられる当時の記録は見つかっていない。信濃へ進出して村上義清らを追ったことから上杉謙信と対立し、1553年から1564年にかけて川中島で数度戦った。1568年に駿河へ侵攻し、1572年に西へ向かって三方ヶ原で徳川家康を破ったが、翌1573年に病没した。',
      3: '甲斐守護武田氏の当主。天文10年(1541)、家臣層の支持を得て父信虎を駿河の今川氏のもとへ追放し、家督を継いだ。天文16年(1547)に分国法「甲州法度之次第」を制定し、私闘（喧嘩）の当事者双方を処罰する喧嘩両成敗、他国との無断の音信の禁止などを定めた。末尾には、当主自身が法に背いた場合は身分の上下を問わず訴え出てよいとする条文が置かれている。統治面では、釜無川・御勅使川の合流点における治水（信玄堤）、黒川金山などの鉱山経営と甲州金（碁石金）の鋳造、伝馬制度の整備などが知られる。八ヶ岳南麓に残る直線的な道は「棒道」と呼ばれ、信玄が信濃侵攻のために開削したと伝えられてきたが、これを裏づける同時代史料はない（caveat 参照）。軍事面では信濃へ進出し、村上義清らを追ったことで越後の長尾景虎（上杉謙信）と対立、天文22年(1553)から永禄7年(1564)まで川中島で数度対陣した。永禄11年(1568)に今川氏の領国駿河へ侵攻し、甲相駿三国同盟は崩れた。元亀3年(1572)に西上して三方ヶ原で徳川家康を破ったが、元亀4年／天正元年(1573)に信濃で病没した。武田氏の事績を伝える『甲陽軍鑑』は江戸初期に成立した軍学書で、史料としての性格については長く議論があり、記述の取捨に注意が要る。',
    },
    year: [1521, 1573],
    keywords: ['武田信玄', '甲斐', '甲州法度之次第', '信玄堤', '川中島', '金山'],
    caveat:
      '武田氏の事績の多くは江戸初期成立の『甲陽軍鑑』に依っており、記述をそのまま史実とみなせない部分があります。「武田の騎馬隊」も一次史料では確認しにくく、主力は徒歩で長槍を用いる兵だったとする見方が現在は強いです。「甲州法度之次第」は26か条を原型とし後に55か条へ増補されたとする理解が長く通説でしたが、26か条本は筆跡や内容から後世の抄出であって55か条が当初の形だとする説も出されています。「棒道」を信玄が開いたとする確かな同時代史料もありません。この語の確実な初見は江戸初期・慶安4年(1651)の「逸見筋小淵沢村四ヶ村山論裁許絵図」で、3筋の棒道を記す『甲斐国志』は文化11年(1814)の成立、そこにも信玄が開いたとは書かれていません。もとからあった道を改修したものとみる説もあります。信玄堤として残る堤防の多くも江戸時代以降の改修によるものです。',
    gameNote:
      '能力は docs/01 §8 の「自領で守るとき手勢札+1」です。常設の補正を手勢札の枚数で表すのは docs/06 §4 の実測によるもので、役を1段階上げる補正は防御側+1だけで勝率が50.6%から82.8%へ飛び、許容上限の75.8%を超えてしまうため使いません。山国の防御の高さは武将能力ではなく、甲斐・信濃の地の利（戦場札2枚が弓になる）と切取に必要な兵で表しているので、能力には重ねていません。なお、武田氏を「戦に強い家」としてだけ描かないよう、史実メモでは法度と治水を中心に置いています。',
  },
};

/** 越後(19)から始まる。合戦の名分（義）を軍略コストで表現している。 */
export const KENSHIN: Warlord = {
  id: 'wl:kenshin',
  name: '上杉謙信',
  reading: 'うえすぎ けんしん',
  clan: '上杉氏（越後長尾氏）',
  color: '#1B3E6F', // 藍色。毘沙門天の旗にちなむ
  startSquare: 19,
  ability: {
    name: '関東管領',
    text: '開始時に軍略を1得る。攻めるときは手勢札が1枚増える。合戦に勝つと威信+2・兵+1・石高+1。ただし自分から仕掛けるには名分が要り、攻撃側になるとき軍略を1消費する。',
    // 威信は「勝ったとき」、軍略は「仕掛けるとき」。同じ配列に並べると
    // どちらも開局時に発火してしまい、能力の意味が消える
    // 「攻めるのに軍略1」は実費で、威信+2（天下点 0.67 相当）では釣り合わない。
    // AI 200局で勝率 8.0% と最下位だったので、勝ち戦で兵も集まる形にした。
    rules: [
      // 費用（攻めるのに軍略1）は確実に払うのに、報酬は勝ったときだけ。
      // 600局で 18.3% と最下位だったので、勝ち戦が次の切り取りに
      // つながるよう石高も足した（武田・島津と同じ「取る力への接続」）
      {
        when: 'win',
        effects: [
          { t: 'gain', res: 'ishin', n: 2 },
          { t: 'gain', res: 'hei', n: 1 },
          { t: 'gain', res: 'koku', n: 1 },
        ],
      },
      { when: 'attack', effects: [{ t: 'lose', res: 'gunryaku', n: 1 }] },
      // 「名分」を払い続けられるだけの軍略を最初に持たせる
      { when: 'start', effects: [{ t: 'gain', res: 'gunryaku', n: 1 }] },
    ],
    // **上杉だけ手勢札の補正を持っていなかった。**
    // 600局の内訳で 攻勝率 33.5% / 守勝率 49.3% とどちらも最下位。
    // 「勝ったら報酬」だけでは、勝てないので報酬が一度も入らない。
    // 名分（軍略1）を払って攻める以上、その攻めは強い、という形にする
    holeCardRules: [{ when: 'attack', n: 1 }],
  },
  note: {
    summary:
      '越後守護代長尾氏の出身。兄から家督を継いで国内をまとめ、1561年に上杉憲政から山内上杉家の家督と関東管領職を譲られた。青苧の交易と湊の再興で国を富ませ、関東と信濃へ繰り返し出兵した。',
    detail: {
      1: '越後（いまの新潟県）の大名です。はじめは長尾景虎という名前でした。関東管領という役目をゆずられ、上杉とよばれるようになりました。青苧という布の原料をあきなって国を豊かにし、武田信玄と川中島で何度も戦いました。',
      2: '越後守護代長尾為景の子。名ははじめ景虎、のち上杉政虎・輝虎と改め、謙信は法号。1548年に兄晴景から家督を継ぎ、対立していた国人たちをまとめた。1561年、関東管領上杉憲政から山内上杉家の家督と関東管領職を譲られ、鎌倉の鶴岡八幡宮で継承の儀を行って上杉政虎と名乗った。越後は青苧（からむしから取る繊維）の産地で、直江津や柏崎の湊から日本海の船で敦賀・小浜へ渡り、琵琶湖の水運を経て京や大坂へ運ばれた。謙信は湊町の税を軽くして人と商人を集める一方、青苧の流通には課税し、これが出兵の費用を支えた。関東と信濃へ繰り返し兵を出し、武田信玄と川中島で数度戦った。1578年に死去すると、後継をめぐって御館の乱が起きた。',
      3: '越後守護代長尾為景の子。初名は景虎、永禄4年(1561)に上杉政虎、のち輝虎と改名し、謙信は法号である。天文17年(1548)に兄晴景から家督を譲られ、守護上杉定実の下で国内の国人層をまとめた。永禄4年(1561)閏3月、関東管領上杉憲政から山内上杉氏の家督と関東管領職を譲られ、鎌倉鶴岡八幡宮で継承の儀を行った。以後、関東管領の名分のもとで関東へ繰り返し越山し、北条氏と対峙した。経済基盤は青苧である。魚沼などで生産された青苧は信濃川水系の舟運で集められ、柏崎・直江津から日本海航路で敦賀・小浜へ、さらに琵琶湖水運を経て京へ運ばれた。越後は京都の青苧座と結びつき、蔵田氏ら商人を通じて流通が統制された。謙信は湊町の課税を軽くして人と物を集めつつ、青苧など収益の大きい産品には課税するという使い分けを行っている。信濃をめぐって武田信玄と天文22年(1553)から永禄7年(1564)まで川中島で数度対陣し、天正6年(1578)に春日山城で死去した。後継を定めないままの死であったため、養子の景勝と景虎が争う御館の乱が起きている。',
    },
    year: [1530, 1578],
    keywords: ['上杉謙信', '越後', '関東管領', '青苧', '川中島', '直江津'],
    caveat:
      '「義のためだけに戦った武将」という人物像は、江戸時代の軍記物を通じて広まったもので、実際の出兵には領国の利害や関東の国人からの要請が関わっています。「敵に塩を送った」という逸話にも確かな同時代史料はありません。また、当時の軍勢が出兵先で略奪や人の売買を行った記録が各地に残っており、上杉軍もその例外ではなかったことが研究で指摘されています。',
    gameNote:
      '「自分から仕掛けるには軍略1が必要」という制約は、名分を重んじたとされる人物像を遊びに落とし込んだものです。ただしその人物像自体が後世に形づくられた面が大きいため（caveat 参照）、史実の再現ではなくゲーム上の制約として扱ってください。',
  },
};

/** 相模(10)から始まる。docs/01 §8 の「城の手勢札ボーナス+1」を持つ。 */
export const UJIYASU: Warlord = {
  id: 'wl:ujiyasu',
  name: '北条氏康',
  reading: 'ほうじょう うじやす',
  clan: '後北条氏',
  color: '#2C6E49', // 深緑。三つ鱗紋
  startSquare: 10,
  ability: {
    name: '所領役帳',
    text: '城のある領国で守るとき、手勢札が1枚増える（居城なら+2枚、天守なら+3枚）。築城の費用が1安い。開始時に銭を1得る。',
    // 「開始時に支城を1つ無償で築く」は実装できない。開局時に持っている国は
    // 本拠地だけで、そこにはすでに居城が建っており、支城を建てると格が下がる。
    // 築城1回ぶんの銭（砦は1銭）に置き換えた。gameNote にも明記してある。
    rules: [{ when: 'start', effects: [{ t: 'gain', res: 'zeni', n: 1 }] }],
    holeCardRules: [{ when: 'defendCastle', n: 1 }],
    castleDiscount: 1,
  },
  note: {
    summary:
      '相模小田原を本拠とした後北条氏の3代目。1546年の河越城の戦いで両上杉氏と古河公方の連合軍を退けた。1550年に雑税を整理する公事赦免令を出し、1559年には家臣の所領と負担を記した『小田原衆所領役帳』を作らせた。',
    detail: {
      1: '相模（いまの神奈川県）の小田原を本きょとした大名です。1546年、河越の城をめぐる戦いで大きな軍をしりぞけました。ばらばらだった年貢や税をわかりやすく整理し、百姓が直接うったえを出せるしくみもつくりました。',
      2: '後北条氏の3代目。1546年、河越城を囲んだ山内上杉憲政・扇谷上杉朝定・古河公方足利晴氏の連合軍を破った（河越城の戦い。河越夜戦とも呼ばれる）。1549年の地震や凶作で百姓の逃散が広がったことを受け、1550年に公事赦免令を出して複雑だった雑税を整理し、段銭と懸銭にまとめた。代官が勝手に税を取ることを防ぐため、虎の印判を押した文書のない賦課を無効とし、百姓が直接訴え出る目安の制度も整えた。1559年には家臣ごとの所領の場所・貫高と、負担すべき兵・馬・鉄砲・槍などを書き上げた『小田原衆所領役帳』を作らせている。1554年には武田・今川と同盟を結んだ（甲相駿三国同盟）。1571年に死去した。',
      3: '後北条氏3代目。天文15年(1546)、河越城を包囲した山内上杉憲政・扇谷上杉朝定・古河公方足利晴氏の連合軍を退け、扇谷上杉氏は当主朝定を失って滅んだ。統治面では、天文18年(1549)の地震と不作による百姓の逃散・田畑の荒廃を受け、翌天文19年(1550)に公事赦免令を発した。これは伊豆・相模・武蔵南部に及んでいた雑多な公事を整理し、貫高の6%にあたる段銭と4%にあたる懸銭に一本化するもので、棟別銭も引き下げられた。あわせて、虎の印判を押した文書によらない賦課を無効とし、百姓が代官を飛び越えて直接訴え出られる目安の制度を整えた。永禄2年(1559)には家臣団を12の衆に編成し、各人の所領の所在と貫高、負担すべき軍役（人数・馬・鉄砲・槍・弓・旗）を記した『小田原衆所領役帳』を作らせている。この帳簿は貫高制による軍役賦課の実態を伝える史料として重視されるが、原本は失われ、江戸時代の写本によって伝わる。ほかに検地の実施、永楽銭を基準とする貨幣の統一、遠江の榛原枡を公定枡として領国の度量衡を統一したこと（所領役帳の編纂に関わり蔵入を管掌した安藤良整が普及させたことから安藤枡とも呼ばれる）、伝馬制度の整備なども知られる。永禄4年(1561)には上杉謙信に小田原城を囲まれ、永禄12年(1569)には武田信玄の侵攻を受けた。元亀2年(1571)に死去。',
    },
    year: [1515, 1571],
    keywords: ['北条氏康', '相模', '小田原', '公事赦免令', '小田原衆所領役帳', '目安'],
    caveat:
      '河越城の戦いについて広く語られる「8万の大軍を1万で夜襲して破った」という経過は、江戸時代の軍記物によって広まったもので、同時代の史料は乏しいものです。氏康自身の書状ではこの戦いを「砂窪（砂久保）の戦い」と記しており、規模や夜戦であったことを疑う説、戦いそのものの実在を疑う説もあります。『小田原衆所領役帳』も原本は現存せず江戸期の写本で伝わります。',
    gameNote:
      '能力は docs/01 §8 の「城の建設費-1。居城・天守の手勢札ボーナスが+1される」です。docs/06 §4 のとおり常設の補正はすべて手勢札の枚数で表すため、氏康の守りの強さは役の段階ではなく城のボーナス加算（居城+2枚／天守+3枚、合計は4枚まで）で効きます。「城の建設費-1」は Effect 型で費用の割引を表現できないため、開始時に支城を1つ無償で築く形に置き換えています。実際の後北条氏の強みは小田原城の堅固さだけでなく、各地に支城を置いて地域ごとに統治した仕組みと、税と訴訟の制度を整えた点にあります。',
  },
};

/** 東日本の武将4名。選択画面の並びに使う。 */
export const EAST_WARLORDS: readonly Warlord[] = [NOBUNAGA, SHINGEN, KENSHIN, UJIYASU];
